import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Check, ArrowRight, User, Phone, Mail, MapPin, Briefcase, FileText, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navbar } from '../components/Navbar';
import AddressAutocomplete from '../components/AddressAutocomplete';

function SalespersonApplicationPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingResume, setCheckingResume] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [resumeInfo, setResumeInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    city: '',
    previous_experience: '',
    agreed_independent_contractor: false,
    agreed_own_taxes: false,
    agreed_terms_conditions: false
  });

  // Check for resume parameter on page load
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (resumeId) {
      checkResumeApplication(resumeId);
    }
  }, [searchParams]);

  const checkResumeApplication = async (resumeId) => {
    setCheckingResume(true);
    try {
      const response = await api.get(`/applications/salesperson/resume/${resumeId}`);
      const data = response.data;
      
      if (data.can_resume) {
        setApplicationId(resumeId);
        setResumeInfo(data);
        
        const questionsRes = await api.get('/applications/assessment/salesperson');
        setAssessmentQuestions(questionsRes.data.questions);
        setAnswers(new Array(questionsRes.data.questions.length).fill(-1));
        
        setStep(2);
        toast.success(`Welcome back, ${data.full_name}! Continue your assessment.`);
      } else {
        setResumeInfo(data);
        if (data.status === 'pending_review') {
          toast.info('Your assessment is complete and under review.');
        } else if (data.status === 'approved') {
          toast.success('Your application has been approved!');
        } else if (data.status === 'rejected') {
          toast.error('Your application was not successful.');
        }
      }
    } catch (error) {
      console.error('Resume check error:', error);
      toast.error('Could not find application. Please start a new application.');
    } finally {
      setCheckingResume(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const canSubmit = () => {
    return formData.full_name && formData.phone && formData.email && formData.city &&
      formData.agreed_independent_contractor && formData.agreed_own_taxes && formData.agreed_terms_conditions;
  };

  const handleSubmitApplication = async () => {
    if (!canSubmit()) {
      toast.error('Please fill in all required fields and agree to all terms');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/applications/salesperson', formData);
      setApplicationId(response.data.application_id);
      
      const questionsRes = await api.get('/applications/assessment/salesperson');
      setAssessmentQuestions(questionsRes.data.questions);
      setAnswers(new Array(questionsRes.data.questions.length).fill(-1));
      
      setStep(2);
      toast.success('Application submitted! Check your email for a link to resume later if needed.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const handleSubmitAssessment = async () => {
    if (answers.includes(-1)) {
      toast.error('Please answer all questions');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/applications/assessment/submit', {
        application_id: applicationId,
        answers: answers
      });
      
      setAssessmentResult(response.data);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO page="salespersonApplication" />
      <div>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-4">
            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > 1 ? <Check className="w-5 h-5" /> : '1'}
                </div>
                <div className={`w-20 h-1 mx-2 ${step > 1 ? 'bg-blue-500' : 'bg-slate-200'}`} />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 2 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > 2 ? <Check className="w-5 h-5" /> : '2'}
              </div>
              <div className={`w-20 h-1 mx-2 ${step > 2 ? 'bg-blue-500' : 'bg-slate-200'}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 3 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                3
              </div>
            </div>
            <div className="flex justify-between text-sm text-slate-500 mt-4 px-4">
              <span>{t('driverApp.application')}</span>
              <span>{t('driverApp.assessment')}</span>
              <span>{t('driverApp.result')}</span>
            </div>
          </div>

          {/* Step 1: Application Form */}
          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="salesperson-application-form">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('salesApp.title')}</h1>
              <p className="text-slate-500 mb-6">{t('salesApp.subtitle')}</p>

              <div className="space-y-6">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-slate-400" />
                    {t('driverApp.fullName')} *
                  </Label>
                  <Input name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder={t('driverApp.enterFullName')} data-testid="sales-name-input" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {t('driverApp.phoneNumber')} *
                  </Label>
                  <Input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+44 7XXX XXXXXX" data-testid="sales-phone-input" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {t('driverApp.email')} *
                  </Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" data-testid="sales-email-input" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {t('driverApp.cityArea')} *
                  </Label>
                  <AddressAutocomplete
                    value={formData.city}
                    onChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                    onSelect={(address) => setFormData(prev => ({ ...prev, city: address?.address || address?.city || formData.city }))}
                    placeholder={t('driverApp.cityPlaceholder')}
                    data-testid="sales-city-input"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    {t('salesApp.previousExperience')}
                  </Label>
                  <textarea name="previous_experience" value={formData.previous_experience} onChange={handleInputChange} placeholder={t('salesApp.experiencePlaceholder')} rows={4} className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-testid="sales-experience-input" />
                </div>

                {/* Contractor Agreement Section */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-500" />
                    {t('driverApp.contractorAgreement')}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {t('salesApp.contractorIntro')}
                  </p>
                  
                  <div className="space-y-3 bg-slate-50 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="agreed_independent_contractor" checked={formData.agreed_independent_contractor} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-500 focus:ring-blue-500" />
                      <span className="text-slate-700 text-sm">{t('driverApp.confirmContractor')}</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="agreed_own_taxes" checked={formData.agreed_own_taxes} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-500 focus:ring-blue-500" />
                      <span className="text-slate-700 text-sm">{t('driverApp.confirmTaxes')}</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="agreed_terms_conditions" checked={formData.agreed_terms_conditions} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-500 focus:ring-blue-500" />
                      <span className="text-slate-700 text-sm">{t('salesApp.confirmTerms').split('Salesperson')[0]}<Link to="/contractor-terms" target="_blank" className="text-blue-500 underline">{t('salesApp.confirmTerms').includes('Términos') ? 'Términos y Condiciones del Vendedor' : 'Salesperson Terms & Conditions'}</Link>. *</span>
                    </label>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 text-sm text-slate-600">
                  <h4 className="font-semibold text-slate-800 mb-2">{t('salesApp.commissionTitle')}</h4>
                  <p>{t('salesApp.commissionDesc')}</p>
                </div>

                <Button onClick={handleSubmitApplication} disabled={loading || !canSubmit()} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6" data-testid="submit-sales-application-btn">
                  {loading ? t('checkout.processing') : t('driverApp.continueAssessment')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Assessment */}
          {step === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="salesperson-assessment">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('salesApp.assessmentTitle')}</h1>
              <p className="text-slate-500 mb-6">{t('salesApp.passMark')}</p>

              <div className="space-y-6">
                {assessmentQuestions.map((q, qIdx) => (
                  <div key={qIdx} className="p-4 border border-slate-200 rounded-xl">
                    <p className="font-medium text-slate-800 mb-3">{qIdx + 1}. {q.question}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <button key={oIdx} type="button" onClick={() => handleAnswerSelect(qIdx, oIdx)} className={`w-full text-left p-3 rounded-lg border transition-all ${answers[qIdx] === oIdx ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300'}`}>
                          {String.fromCharCode(65 + oIdx)}. {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <Button onClick={handleSubmitAssessment} disabled={loading || answers.includes(-1)} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6">
                  {loading ? t('checkout.processing') : t('driverApp.submitAssessment')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 3 && assessmentResult && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="sales-assessment-result">
              {assessmentResult.passed ? (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-10 h-10 text-green-500" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('driverApp.passed')}</h1>
                  <p className="text-slate-500 mb-4">{t('driverApp.score')}: {assessmentResult.score}/{assessmentResult.total}</p>
                  <p className="text-slate-600 mb-8">{assessmentResult.message}</p>
                  <div className="bg-blue-50 rounded-xl p-6 text-left">
                    <h3 className="font-semibold text-slate-800 mb-2">{t('driverApp.whatNext')}</h3>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li>{t('salesApp.nextStep1')}</li>
                      <li>{t('salesApp.nextStep2')}</li>
                      <li>{t('salesApp.nextStep3')}</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center text-4xl">😔</div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('driverApp.notPassed')}</h1>
                  <p className="text-slate-500 mb-4">{t('driverApp.score')}: {assessmentResult.score}/{assessmentResult.total}</p>
                  <p className="text-slate-600 mb-8">{assessmentResult.message}</p>
                  <Button onClick={() => navigate('/')} variant="outline">{t('driverApp.returnHome')}</Button>
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SalespersonApplicationPage;
